import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Modal,
  TextField,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "components/snackbar/SnackbarContext";
import { formatDate } from "utils/formatDate";
import { reviewPresentation } from "../reviewPresentation";
import DocumentPreview from "./DocumentPreview";
import DocumentActions from "./DocumentActions";
import DocumentStatus from "./DocumentStatus";
import {
  getReviewDocument,
  getReviewHistory,
  getReviewFile,
  reviewDocument,
} from "../service/DocumentReviewService";
import { useCompanyDocumentsLanguage } from "../i18n/CompanyDocumentsLanguageContext";

export default function DocumentReviewModal({ documentId, onClose, onSaved }) {
  const initialSelection = useRef(true);
  const [id, setId] = useState(documentId);
  const [record, setRecord] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const { setMessage } = useSnackbar();
  const { t } = useCompanyDocumentsLanguage();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setDecision("");
    setNote("");
    Promise.all([getReviewDocument(id), getReviewHistory(id)])
      .then(([row, versions]) => {
        if (active) {
          if (initialSelection.current) {
            initialSelection.current = false;
            const latest = versions.find((v) => v.is_latest);
            if (latest && latest.id !== id) { setId(latest.id); return; }
          }
          setRecord(row);
          setHistory(versions);
        }
      })
      .catch((e) => active && setError(e.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id]);
  const confirm = async () => {
    if (!record?.can_review || (decision === "NEED_REVISION" && !note.trim())) return;
    setBusy(true);
    setError("");
    try {
      const updated = await reviewDocument(id, decision, note.trim());
      setRecord(updated);
      setDecision("");
      setNote("");
      setHistory(await getReviewHistory(id));
      setMessage({
        type: "success",
        desc: t("adminReview.documents.saveSuccess"),
      });
      await onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      onClose={busy ? undefined : onClose}
      aria-labelledby="document-review-title"
      sx={{ zIndex: 1000001 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "calc(100% - 24px)", md: 850 },
          maxWidth: "calc(100vw - 24px)",
          maxHeight: "calc(100vh - 24px)",
          boxSizing: "border-box",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          p: 3,
        }}
      >
        <h2 id="document-review-title" className="text-xl font-bold mb-4">
          {t("adminReview.documents.title")}
        </h2>
        {error && <Alert severity="error">{error}</Alert>}
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          record && (
            <div className="grid gap-4">
              {!record.is_latest && (
                <Alert severity="warning">
                  {t("adminReview.documents.historical")}
                </Alert>
              )}
              <div className="flex justify-between gap-3">
                <div>
                  <h3 className="font-semibold">
                    {record.company_name} · {record.company_code}
                  </h3>
                  <p>
                    {reviewPresentation(record, t).name} · v
                    {record.version}{" "}
                    (
                    {record.is_latest
                      ? t("adminReview.common.latest")
                      : t("adminReview.common.previous")}
                    )
                  </p>
                </div>
                <DocumentStatus status={record.status} />
              </div>
              <p>{reviewPresentation(record, t).context}</p>
              <div className="text-sm break-words">
                <p>
                  {t("adminReview.common.filename")}:{" "}
                  {record.stored_filename}
                </p>
                <p>
                  {t("adminReview.common.uploadedBy")}:{" "}
                  {record.uploaded_by_name ||
                    t("adminReview.common.user", {
                      id: record.uploaded_by,
                    })}
                </p>
                <p>
                  {t("adminReview.common.uploaded")}:{" "}
                  {formatDate.toFullDateTime(record.uploaded_at)}
                </p>
              </div>
              {record.reviewed_at && (
                <div className="text-sm">
                  <p>
                    {t("adminReview.common.reviewedBy")}:{" "}
                    {record.reviewed_by_name ||
                      t("adminReview.common.admin", {
                        id: record.reviewed_by,
                      })}
                  </p>
                  <p>
                    {t("adminReview.common.reviewed")}:{" "}
                    {formatDate.toFullDateTime(record.reviewed_at)}
                  </p>
                  {record.review_note && (
                    <p className="whitespace-pre-wrap break-words">
                      {t("adminReview.common.noteFromSigu")}:{" "}
                      {record.review_note}
                    </p>
                  )}
                </div>
              )}
              <div>
                <DocumentActions document={record} getFile={getReviewFile} showView={false} downloadVariant="outlined" />
              </div>
              <DocumentPreview key={record.id} record={record} />
              {record.can_review && record.is_latest && record.status === "SUBMITTED" && !decision && (
                <div className="flex gap-3">
                  <Button
                    variant="outlined"
                    onClick={() => setDecision("NEED_REVISION")}
                  >
                    {t("adminReview.documents.needRevision")}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setDecision("VERIFIED")}
                  >
                    {t("adminReview.documents.verify")}
                  </Button>
                </div>
              )}
              {record.can_review && decision && (
                <div className="border rounded-lg p-4 grid gap-3">
                  <h3 className="font-semibold">
                    {decision === "VERIFIED"
                      ? t(
                          "adminReview.documents.verifyQuestion",
                          { version: record.version },
                        )
                      : t(
                          "adminReview.documents.revisionQuestion",
                          { version: record.version },
                        )}
                  </h3>
                  {decision === "NEED_REVISION" && (
                    <TextField
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
                          alignItems: "flex-start",
                          padding: "16.5px 14px",
                        },
                        "& .MuiInputBase-inputMultiline": {
                          padding: "0 !important",
                        },
                      }}
                      label={t("adminReview.documents.revisionNote")}
                      required
                      multiline
                      minRows={3}
                      inputProps={{ maxLength: 2000 }}
                      helperText={t(
                        "adminReview.documents.revisionHelper",
                      )}
                      value={note}
                      disabled={busy}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  )}
                  <div className="flex gap-3">
                    <Button
                      disabled={busy}
                      onClick={() => {
                        setDecision("");
                        setNote("");
                      }}
                    >
                      {t("adminReview.documents.cancel")}
                    </Button>
                    <Button
                      variant="contained"
                      disabled={
                        busy ||
                        (decision === "NEED_REVISION" && !note.trim()) ||
                        Array.from(note).length > 2000
                      }
                      onClick={confirm}
                    >
                      {decision === "VERIFIED"
                        ? t("adminReview.documents.confirmVerify")
                        : t("adminReview.documents.confirmRevision")}
                    </Button>
                  </div>
                </div>
              )}
              <div className="border-t pt-3">
                <h3 className="font-semibold">
                  {t("adminReview.documents.history")}
                </h3>
                {history.map((version) => (
                  <div
                    key={version.id}
                    className="flex flex-wrap justify-between gap-2 py-2 border-b"
                  >
                    <div>
                      <p>
                        v{version.version} ·{" "}
                        {version.is_latest
                          ? t("adminReview.common.latest")
                          : t("adminReview.common.previous")}
                      </p>
                      <p className="text-sm break-all">{version.stored_filename}</p>
                      <DocumentStatus status={version.status} />
                      <p className="text-xs">
                        {t("adminReview.common.uploaded")}:{" "}
                        {formatDate.toFullDateTime(version.uploaded_at)} ·{" "}
                        {version.uploaded_by_name ||
                          t("adminReview.common.user", {
                            id: version.uploaded_by,
                          })}
                      </p>
                      {version.reviewed_at && (
                        <p className="text-xs">
                          {t("adminReview.common.reviewed")}:{" "}
                          {formatDate.toFullDateTime(version.reviewed_at)} ·{" "}
                          {version.reviewed_by_name ||
                            t("adminReview.common.admin", {
                              id: version.reviewed_by,
                            })}
                        </p>
                      )}
                      {version.review_note && (
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {version.review_note}
                        </p>
                      )}
                    </div>
                    <Button
                      disabled={busy || version.id === id}
                      onClick={() => setId(version.id)}
                    >
                      {t(
                        "adminReview.documents.openVersion",
                        { version: version.version },
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )
        )}
        <div className="flex justify-end mt-4">
          <Button disabled={busy} onClick={onClose}>
            {t("adminReview.common.close")}
          </Button>
        </div>
      </Box>
    </Modal>
  );
}
